import React from 'react';
import PropTypes from 'prop-types';
import AppTable from 'components/AppTable';
import ModalBusyContainer from 'components/ModalBusyContainer';
import withTableProps from 'HOC/types/withTable';
import { useTranslation } from 'react-i18next';
import { useAsyncFn } from 'react-use';
import { useHistory } from 'react-router-dom';
import { transformTableData, getConfirmationModalConfig } from 'utils';
import { createTrainingSessionEvidence, setTrainingSessionParticipantFailed } from 'http/trainingSessions';
import { useDispatch } from 'react-redux';
import { actions as modalActions } from 'redux/modal';
import { getSessionsInProgressConfig } from './helpers/getSessionTablesConfig';
import { formatTrainingModuleLabel, formatTrainingPartLabel } from './helpers/formatTrainingLabels';

const SessionsInProgressTable = ({ tableData, helpers, userId, routesAliases }) => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const history = useHistory();
  const handleCloseModal = () => dispatch(modalActions.closeModal());

  const [settingsParticipantStatus, setParticipantStatus] = useAsyncFn(async (sessionId, status) => {
    let data;
    if (status === 'passed') {
      data = await createTrainingSessionEvidence(sessionId, userId);
    } else {
      data = await setTrainingSessionParticipantFailed(sessionId, userId);
    }
    if (!data.error && !(data.statusCode && data.statusCode >= 400)) {
      handleCloseModal();
      tableData.handleForceUpdate();
      history.push(`${routesAliases.editStudent}/${userId}`, {
        trainingSessionTabIndex: 3,
        trainingSessionAlias: 'past',
      });
    }
    return data;
  });

  const handleToggleState = (sessionId, status) => {
    const title = status === 'failed' ? t('students.modalSessionFailedTitle') : t('students.modalSessionPassedTitle');
    const message =
      status === 'failed' ? t('students.modalSessionFailedMessage') : t('students.modalSessionPassedMessage');
    const [config, element] = getConfirmationModalConfig({
      title,
      confirmAction: () => setParticipantStatus(sessionId, status),
      rejectAction: handleCloseModal,
      message,
    });
    dispatch(modalActions.openModal(config, element));
  };

  const rows = (Array.isArray(tableData.data) ? tableData.data : []).map(item => ({
    ...item,
    trainingModuleLabel: formatTrainingModuleLabel(item.trainingModuleCode, item.trainingModuleName),
    trainingPartLabel: formatTrainingPartLabel(item.trainingPartVersionCode, item.trainingMethod),
    expirationDate: item.expirationDate ?? item.evidenceExpiresAt ?? null,
    isMoodleSession: item.isMoodleSession ?? item.deliveryMethod === 'elearning',
  }));

  const tableConfig = getSessionsInProgressConfig({ handleToggleState });
  const [columns, tableRows] = transformTableData(tableConfig, rows);

  const tableActions = [
    {
      type: 'icon',
      icon: 'edit',
      onClick: helpers.editSession,
    },
    {
      type: 'icon',
      icon: 'delete',
      color: 'error',
      onClick: event => {
        helpers.removeParticipant(event);
        tableData.handleForceUpdate();
      },
    },
  ];

  return (
    <ModalBusyContainer busy={settingsParticipantStatus.loading}>
      <AppTable
        columns={columns}
        data={tableRows}
        totalCount={tableData.totalCount}
        rowsPerPage={tableData.pageSize}
        isLazyLoading
        onChangePage={tableData.handleChangePage}
        onColumnSorting={tableData.handleSorting}
        actions={tableActions}
        extraActionsKeys={['stationId']}
      />
    </ModalBusyContainer>
  );
};

SessionsInProgressTable.propTypes = {
  tableData: PropTypes.shape(withTableProps).isRequired,
  helpers: PropTypes.shape({
    editSession: PropTypes.func,
    removeParticipant: PropTypes.func,
  }).isRequired,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  routesAliases: PropTypes.shape({
    editStudent: PropTypes.string.isRequired,
  }).isRequired,
};

export default SessionsInProgressTable;
